import { useEffect, useRef, useState, useCallback } from "react";
import { terminalApi, unifiedLoopApi, laborApi, venturesApi } from "@/lib/api-client";
import {
  Terminal, Radio, ShieldAlert, Zap, ScanSearch, PenLine, Coins, Bot,
  ArrowRight, Ban, ExternalLink, Satellite, Check, Pause, Search, Send, Loader2, Hammer, Mic, Square, X,
} from "lucide-react";
import { toast } from "sonner";

const AGENT_ICON = {
  scout: Radio, sentinel: ShieldAlert, executor: Zap, auditor: ScanSearch,
  creator: PenLine, controller: Coins, strategist: Bot, system: Terminal,
};
const KIND_COLOR = {
  info: "text-white/60", success: "text-emerald-400", warn: "text-amber-400",
  blocked: "text-red-400", handoff: "text-teal-300",
};
const KIND_PREFIX = { info: "◆", success: "✓", warn: "⚠", blocked: "⛔", handoff: "→" };
const SEV_BAR = {
  critical: "border-l-red-500", high: "border-l-amber-400",
  warning: "border-l-amber-400", medium: "border-l-teal-400",
  info: "border-l-white/20", low: "border-l-white/20",
};

function QuickReply({ item, onSent }) {
  const [busy, setBusy] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(item.quick_replied ? "sent" : null);

  const fire = async (command, text = "") => {
    setBusy(true);
    try {
      const r = await terminalApi.quickReply(item.id, command, text);
      setSent(command);
      setCustomOpen(false);
      toast.success(`Directive injected into ${r.agent}'s context`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick-reply failed");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="flex items-center gap-1.5 text-[10px] font-mono-uc uppercase tracking-wider text-teal-300 mt-2" data-testid={`quick-replied-${item.id}`}>
        <Check size={11}/> Directive injected → {item.agent}
      </div>
    );
  }
  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {busy ? <Loader2 size={12} className="animate-spin text-white/40"/> : (
          <>
            <button data-testid={`quick-reply-proceed-${item.id}`} onClick={() => fire("proceed")}
              className="font-mono-uc text-[9px] uppercase tracking-wider px-2 py-1 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1">
              <Check size={9}/> Proceed
            </button>
            <button data-testid={`quick-reply-hold-${item.id}`} onClick={() => fire("hold")}
              className="font-mono-uc text-[9px] uppercase tracking-wider px-2 py-1 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1">
              <Pause size={9}/> Hold
            </button>
            <button data-testid={`quick-reply-dig-${item.id}`} onClick={() => fire("dig_deeper")}
              className="font-mono-uc text-[9px] uppercase tracking-wider px-2 py-1 border border-teal-500/40 text-teal-300 hover:bg-teal-500/10 flex items-center gap-1">
              <Search size={9}/> Dig Deeper
            </button>
            <button data-testid={`quick-reply-custom-${item.id}`} onClick={() => setCustomOpen((o) => !o)}
              className="font-mono-uc text-[9px] uppercase tracking-wider px-2 py-1 border border-white/15 text-white/60 hover:bg-white/5">
              Command…
            </button>
          </>
        )}
      </div>
      {customOpen && (
        <div className="flex gap-1.5 mt-1.5">
          <input
            data-testid={`quick-reply-input-${item.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && note.trim() && fire("custom", note.trim())}
            placeholder={`Order for ${item.agent}…`}
            className="flex-1 h-8 bg-black border border-white/15 focus:border-amber-500/40 outline-none px-2 text-[11px] text-white/90"
          />
          <button data-testid={`quick-reply-send-${item.id}`} onClick={() => note.trim() && fire("custom", note.trim())}
            disabled={busy || !note.trim()}
            className="w-8 h-8 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 flex items-center justify-center disabled:opacity-40">
            <Send size={11}/>
          </button>
        </div>
      )}
    </div>
  );
}

export default function CommandTerminal() {
  const [mode, setMode] = useState("executive");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState([]);
  const [digest, setDigest] = useState(null);
  const [enforcing, setEnforcing] = useState(false);
  const [mandate, setMandate] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [filing, setFiling] = useState(false);
  const [lastMandate, setLastMandate] = useState(null);
  const [ventures, setVentures] = useState([]);
  const recRef = useRef(null);
  const bodyRef = useRef(null);

  const load = useCallback(async (m) => {
    try {
      const r = await terminalApi.feed(m);
      setItems(r.items || []);
      const [sb, dg] = await Promise.all([
        laborApi.scoreboard(),
        laborApi.digest().catch(() => null),
      ]);
      setBoard(sb.board || []);
      setDigest(dg);
    } catch (e) { console.error("terminal feed failed", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    setLoading(true);
    load(mode);
    const t = setInterval(() => load(mode), mode === "system" ? 5000 : 15000);
    return () => clearInterval(t);
  }, [mode, load]);

  const fireLoop = async () => {
    const iid = window.prompt("Paste a Scout insight ID to run the Unified Loop:");
    if (!iid) return;
    try { await unifiedLoopApi.fireFromInsight(iid.trim()); toast.success("Unified Loop fired"); load(mode); }
    catch (e) { toast.error(e?.response?.data?.detail || "Loop failed"); }
  };

  const enforce = async () => {
    setEnforcing(true);
    try {
      const r = await laborApi.enforce();
      toast.success(`Mandate enforced — ${r.flagged} inefficiencies flagged, ${r.remediated} artifacts auto-produced`);
      load(mode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enforcement failed");
    } finally { setEnforcing(false); }
  };

  const blockedCount = items.filter((e) => e.kind === "blocked").length;

  // VOICE-TO-MANDATE — record via MediaRecorder, transcribe through managed Whisper STT
  const toggleMic = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          const r = await terminalApi.transcribe(blob);
          setMandate((m) => (m ? m + " " : "") + r.text);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed");
        } finally { setTranscribing(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  // ROUTING CORRECTION — one click re-assigns the last-filed mandate
  const reroute = async (patch) => {
    if (!lastMandate) return;
    try {
      const r = await terminalApi.correctRoute(lastMandate.task_id, patch);
      setLastMandate((m) => ({ ...m, routing: { ...m.routing, ...r.routing } }));
      toast.success(`Routing corrected — ${r.routing.venture_name || "Estate-wide"} · ${r.routing.agent}`);
      load(mode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-route failed");
    }
  };

  const fileMandate = async () => {
    const text = mandate.trim();
    if (!text || filing) return;
    setFiling(true);
    try {
      const r = await terminalApi.fileMandate(text, "voice");
      const rt = r.routing || {};
      setLastMandate({ task_id: r.task_id, title: r.title, routing: rt });
      if (!ventures.length) {
        venturesApi.list().then((vs) => setVentures((vs || []).filter((v) => (v.name || "").trim()))).catch(() => {});
      }
      toast.success(
        <span data-testid="toast-mandate-filed">
          {`Mandate filed — routed → ${rt.venture_name || "Estate-wide"} · ${rt.agent || "executor"} · ${(rt.priority || "high").toUpperCase()}`}
        </span>);
      setMandate("");
      load(mode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mandate filing failed");
    } finally { setFiling(false); }
  };

  return (
    <section className="uc-card p-5 uc-fade" data-testid="command-terminal">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="uc-chip mb-2"><Satellite size={11}/> COMMAND TERMINAL</div>
          <h2 className="font-serif-display text-xl leading-tight">Mission Control.</h2>
        </div>
        {blockedCount > 0 && (
          <span data-testid="terminal-blocked-badge" className="uc-chip border-red-500/40 text-red-400 flex items-center gap-1">
            <Ban size={10}/> {blockedCount}
          </span>
        )}
      </div>

      {/* Mode toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex border border-white/15" data-testid="terminal-mode-toggle">
          <button
            data-testid="terminal-mode-executive"
            onClick={() => setMode("executive")}
            className={`font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors ${mode === "executive" ? "bg-amber-500/15 text-amber-400" : "text-white/50 hover:text-white/80"}`}
          >Executive</button>
          <button
            data-testid="terminal-mode-system"
            onClick={() => setMode("system")}
            className={`font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5 border-l border-white/15 transition-colors ${mode === "system" ? "bg-teal-500/15 text-teal-300" : "text-white/50 hover:text-white/80"}`}
          >System</button>
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="terminal-refresh-btn" onClick={() => load(mode)}
            className="font-mono-uc text-[9px] uppercase tracking-widest text-white/50 hover:text-white/80 px-2 py-1 border border-white/10 hover:border-white/20">
            Refresh
          </button>
          {mode === "executive" && (
            <button data-testid="labor-enforce-btn" onClick={enforce} disabled={enforcing}
              className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400 hover:text-amber-200 px-2 py-1 border border-amber-500/40 hover:bg-amber-500/10 disabled:opacity-50 flex items-center gap-1">
              {enforcing ? <Loader2 size={9} className="animate-spin"/> : <Hammer size={9}/>} Enforce Mandate
            </button>
          )}
          {mode === "system" && (
            <button data-testid="terminal-loop-btn" onClick={fireLoop}
              className="font-mono-uc text-[9px] uppercase tracking-widest text-teal-300 hover:text-teal-100 px-2 py-1 border border-teal-500/40 hover:bg-teal-500/10">
              Fire Loop
            </button>
          )}
        </div>
      </div>

      {/* Labor Scoreboard — Infrastructure for Labor: artifacts over reports */}
      {board.length > 0 && (
        <div className="mb-3 border border-white/10 bg-black p-2" data-testid="labor-scoreboard">
          <div className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40 mb-1.5 flex items-center gap-1.5">
            <Hammer size={9} className="text-amber-400"/> Labor Scoreboard · Artifacts over Reports
          </div>
          <div className="grid grid-cols-4 gap-x-2 gap-y-1">
            {board.map((a) => (
              <div key={a.agent} className="flex items-center gap-1" data-testid={`labor-agent-${a.agent}`}>
                <span className="font-mono-uc text-[9px] uppercase tracking-wider text-white/55 truncate">{a.agent.slice(0, 5)}</span>
                <span className={`font-mono-uc text-[9px] ${a.artifact_rate === null ? "text-white/30" : a.artifact_rate >= 0.5 ? "text-emerald-400" : "text-amber-400"}`}>
                  {a.artifact_rate === null ? "—" : `${Math.round(a.artifact_rate * 100)}%`}
                </span>
                {a.inefficiencies > 0 && <span className="font-mono-uc text-[9px] text-red-400">⚠{a.inefficiencies}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inefficiency Digest — weekly flag tally: who is slacking */}
      {mode === "executive" && digest && digest.total_flagged > 0 && (
        <div className="mb-3 border border-red-500/20 bg-black p-2" data-testid="inefficiency-digest">
          <div className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40 mb-1.5 flex items-center gap-1.5">
            <Ban size={9} className="text-red-400"/> Inefficiency Digest · Last 7 days · {digest.total_flagged} flag{digest.total_flagged === 1 ? "" : "s"}
          </div>
          <div className="space-y-1">
            {digest.board.map((a, idx) => (
              <div key={a.agent} className="flex items-center gap-2" data-testid={`digest-agent-${a.agent}`}>
                <span className={`font-mono-uc text-[9px] uppercase tracking-wider w-16 truncate ${idx === 0 ? "text-red-400" : "text-white/55"}`}>
                  {a.agent}{idx === 0 && " ⚠"}
                </span>
                <div className="flex-1 h-1.5 bg-white/5 overflow-hidden">
                  <div className="h-full bg-red-500/60" style={{ width: `${Math.round((a.flagged / digest.board[0].flagged) * 100)}%` }}/>
                </div>
                <span className="font-mono-uc text-[9px] text-white/60 w-20 text-right">
                  {a.flagged} flagged · <span className="text-emerald-400">{a.remediated} fixed</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={bodyRef}
        className={`bg-black border border-white/10 h-[400px] overflow-y-auto uc-scroll ${mode === "system" ? "p-3 space-y-1 font-mono-uc text-[12px]" : "p-2 space-y-2"}`}
        data-testid="terminal-body">
        {loading && <div className="text-white/40 italic text-xs p-1">Streaming…</div>}
        {!loading && items.length === 0 && (
          <div className="text-white/40 italic text-xs p-1">No transmissions yet. Run a sweep or deploy any agent.</div>
        )}

        {!loading && mode === "executive" && items.map((it) => {
          if (it.feed_type === "chatter") {
            const color = KIND_COLOR[it.kind] || "text-teal-300";
            return (
              <div key={it.id} className="flex items-start gap-2 px-2 py-1.5 border border-white/5 bg-white/[0.02] font-mono-uc text-[10px]" data-testid={`feed-chatter-${it.id}`}>
                <span className={`flex-none ${color}`}>{KIND_PREFIX[it.kind] || "→"}</span>
                <span className={`uppercase tracking-widest flex-none ${color}`}>{it.agent}</span>
                {it.next_agent && <span className="text-white/30 flex-none inline-flex items-center gap-0.5"><ArrowRight size={8}/>{it.next_agent}</span>}
                <span className="text-white/60 break-words min-w-0 flex-1">{it.message}</span>
              </div>
            );
          }
          const Icon = AGENT_ICON[it.agent] || Bot;
          return (
            <div key={it.id} className={`border border-white/10 border-l-2 ${SEV_BAR[it.severity] || "border-l-white/20"} bg-white/[0.02] p-2.5`} data-testid={`feed-briefing-${it.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={11} className="text-amber-400/80 flex-none"/>
                <span className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400/80">{it.agent}</span>
                {it.venture_name && <span className="font-mono-uc text-[9px] text-teal-300 truncate">· {it.venture_name}</span>}
                <span className="ml-auto text-white/30 text-[9px] font-mono-uc flex-none">
                  {new Date(it.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <div className="text-[12px] text-white/90 font-medium leading-snug">{it.title}</div>
              <p className="text-[11px] text-white/55 leading-relaxed mt-0.5 line-clamp-3">{it.message}</p>
              {it.status === "pending" && <QuickReply item={it} onSent={() => load("executive")} />}
              {it.status !== "pending" && (
                <div className="text-[9px] font-mono-uc uppercase tracking-wider text-white/35 mt-1.5">{it.status}</div>
              )}
            </div>
          );
        })}

        {!loading && mode === "system" && items.map((ev) => {
          const Icon = AGENT_ICON[ev.agent] || Bot;
          const color = KIND_COLOR[ev.kind] || "text-white/60";
          return (
            <div key={ev.id} className="flex items-start gap-2 py-1 border-b border-white/5 last:border-none" data-testid={`terminal-event-${ev.id}`}>
              <span className={`w-4 flex-none ${color}`}>{KIND_PREFIX[ev.kind] || "·"}</span>
              <Icon size={12} className={`mt-0.5 flex-none ${color}`}/>
              <span className={`uppercase tracking-widest text-[9px] w-14 flex-none ${color}`}>{ev.feed_type === "execution" ? "exec" : ev.agent}</span>
              <span className="flex-1 min-w-0 text-white/85 break-words">
                {ev.message}
                {ev.venture_name && <span className="ml-2 text-teal-300 text-[10px]">· {ev.venture_name}</span>}
                {ev.next_agent && (
                  <span className="ml-2 text-teal-300 text-[10px] inline-flex items-center gap-1"><ArrowRight size={9}/>{ev.next_agent}</span>
                )}
                {ev.artifact_ref && (
                  <span className="ml-2 text-amber-300/90 text-[10px] inline-flex items-center gap-1">
                    <ExternalLink size={9}/> {ev.artifact_ref.kind}:{String(ev.artifact_ref.ref).slice(0, 10)}…
                  </span>
                )}
                {ev.missing_credential && <span className="ml-2 text-red-400 text-[10px]">need [{ev.missing_credential}]</span>}
              </span>
              <span className="text-white/30 text-[10px] flex-none">
                {new Date(ev.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            </div>
          );
        })}
      </div>

      {/* VOICE-TO-MANDATE input — mic records, Whisper transcribes, Send files the mandate */}
      <div className="flex gap-1.5 mt-3" data-testid="terminal-mandate-bar">
        <button
          data-testid="terminal-mic-btn"
          onClick={toggleMic}
          disabled={transcribing}
          title={recording ? "Stop recording" : "Dictate a mandate (Whisper STT)"}
          className={`w-9 h-9 flex-none border flex items-center justify-center transition-colors disabled:opacity-40 ${
            recording
              ? "border-rose-500/60 text-rose-400 bg-rose-500/10 animate-pulse"
              : "border-white/15 text-white/60 hover:border-amber-500/40 hover:text-amber-400"}`}
        >
          {transcribing ? <Loader2 size={13} className="animate-spin"/> : recording ? <Square size={12}/> : <Mic size={13}/>}
        </button>
        <input
          data-testid="terminal-mandate-input"
          value={mandate}
          onChange={(e) => setMandate(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fileMandate()}
          placeholder={recording ? "Recording — speak your mandate…" : transcribing ? "Transcribing…" : "Type or dictate a mandate…"}
          className="flex-1 h-9 bg-black border border-white/15 focus:border-amber-500/40 outline-none px-2 text-[11px] text-white/90"
        />
        <button
          data-testid="terminal-mandate-send-btn"
          onClick={fileMandate}
          disabled={filing || !mandate.trim()}
          className="w-9 h-9 flex-none border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 flex items-center justify-center disabled:opacity-40 transition-colors"
        >
          {filing ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
        </button>
      </div>

      {/* ROUTING CORRECTION — re-assign the last mandate's venture/agent in one click */}
      {lastMandate && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap border border-teal-500/20 bg-black px-2 py-1.5"
             data-testid="mandate-routing-row">
          <span className="font-mono-uc text-[9px] uppercase tracking-widest text-teal-300 flex-none">Routed →</span>
          <span className="font-mono-uc text-[10px] text-white/70 truncate max-w-[30%]" title={lastMandate.title}>
            {lastMandate.title}
          </span>
          <select
            data-testid="reroute-venture-select"
            value={lastMandate.routing?.venture_id || ""}
            onChange={(e) => reroute({ venture_id: e.target.value })}
            className="h-7 bg-black border border-white/15 text-[10px] text-white/85 font-mono-uc outline-none focus:border-amber-500/40 max-w-[130px]"
          >
            <option value="">Estate-wide</option>
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select
            data-testid="reroute-agent-select"
            value={lastMandate.routing?.agent || "executor"}
            onChange={(e) => reroute({ agent: e.target.value })}
            className="h-7 bg-black border border-white/15 text-[10px] text-white/85 font-mono-uc outline-none focus:border-amber-500/40"
          >
            {["scout", "sentinel", "executor", "auditor", "strategist", "creator", "controller"].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <button
            data-testid="reroute-dismiss-btn"
            onClick={() => setLastMandate(null)}
            className="ml-auto text-white/35 hover:text-white/80 transition-colors"
            title="Dismiss"
          >
            <X size={12}/>
          </button>
        </div>
      )}
    </section>
  );
}
